import { css, keyframes } from '@emotion/react';
import React from 'react';
import { confettiZIndex } from 'styles/global_defaults/scaffolding';
import { gray5, gray4, gray3 } from 'styles/global_defaults/colors';
import { lightOrange } from 'athena/styles/colors';

// Animation: Move left and return back (horizontal motion)
const moveLeft = keyframes`
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(-60px);
    opacity: 0.6;
  }
`;

// Animation: Move right and return back (horizontal motion)
const moveRight = keyframes`
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(60px);
    opacity: 0.6;
  }
`;

interface DotConfig {
  id: string;
  cx: number;
  cy: number;
  r: number;
  fill: string;
  animation: ReturnType<typeof keyframes>;
  duration: number;
  delay: number;
}

// First 7 dots: left side particles (move left and return)
// Second 7 dots: right side particles (move right and return)
// Sizes: Large (7px), Medium (5px), Small (3.5px)
// Colors: lightOrange (peachy white), gray4 (medium gray), gray5 (light gray), gray3 (dark gray)
const DOT_CONFIGS: DotConfig[] = [
  // Left side particles (move left)
  { id: 'dot-1', cx: 10, cy: 39, r: 3.5, fill: lightOrange, animation: moveLeft, duration: 4, delay: 0 }, // Large, peachy white
  { id: 'dot-2', cx: 20, cy: 44, r: 3.5, fill: lightOrange, animation: moveLeft, duration: 4.5, delay: 0.5 }, // Large, peachy white
  { id: 'dot-3', cx: 15, cy: 50, r: 2.5, fill: lightOrange, animation: moveLeft, duration: 4.2, delay: 1 }, // Medium, peachy white
  { id: 'dot-4', cx: 30, cy: 41, r: 2.5, fill: lightOrange, animation: moveLeft, duration: 3.8, delay: 1.5 }, // Medium, peachy white
  { id: 'dot-5', cx: 40, cy: 46, r: 1.75, fill: gray4, animation: moveLeft, duration: 4.3, delay: 2 }, // Small, medium gray
  { id: 'dot-6', cx: 35, cy: 50, r: 1.75, fill: gray5, animation: moveLeft, duration: 4, delay: 2.5 }, // Small, light gray
  { id: 'dot-7', cx: 45, cy: 44, r: 1.75, fill: gray3, animation: moveLeft, duration: 4.6, delay: 3 }, // Small, dark gray
  // Right side particles (move right)
  { id: 'dot-8', cx: 55, cy: 44, r: 1.75, fill: gray4, animation: moveRight, duration: 4, delay: 0 }, // Small, medium gray
  { id: 'dot-9', cx: 60, cy: 50, r: 2.5, fill: lightOrange, animation: moveRight, duration: 4.5, delay: 0.5 }, // Medium, peachy white
  { id: 'dot-10', cx: 65, cy: 46, r: 2.5, fill: lightOrange, animation: moveRight, duration: 4.2, delay: 1 }, // Medium, peachy white
  { id: 'dot-11', cx: 70, cy: 41, r: 1.75, fill: gray4, animation: moveRight, duration: 3.8, delay: 1.5 }, // Small, medium gray
  { id: 'dot-12', cx: 80, cy: 44, r: 1.75, fill: gray5, animation: moveRight, duration: 4.3, delay: 2 }, // Small, light gray
  { id: 'dot-13', cx: 85, cy: 50, r: 1.75, fill: gray3, animation: moveRight, duration: 4, delay: 2.5 }, // Small, dark gray
  { id: 'dot-14', cx: 90, cy: 39, r: 1.75, fill: gray4, animation: moveRight, duration: 4.6, delay: 3 }, // Small, medium gray
];

const containerStyles = css`
  .dots-container {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: ${confettiZIndex};
    pointer-events: none;
  }
  .children {
    z-index: ${confettiZIndex + 1};
  }
`;

interface DotsFlowAnimationProps {
  width?: number;
  height?: number;
  className?: string;
  children: React.ReactNode;
}

const DotsFlowAnimation = ({
  width = 350,
  height = 60,
  className,
  children,
}: DotsFlowAnimationProps) => (
  <div
    className={`position-relative d-inline-block ${className || ''}`}
    css={containerStyles}
  >
    <div className='position-absolute dots-container' aria-hidden='true'>
      <svg
        width={width}
        height={height}
        viewBox='0 0 100 60'
      >
        {DOT_CONFIGS.map((dot) => (
          <circle
            key={dot.id}
            cx={dot.cx}
            cy={dot.cy}
            r={dot.r}
            fill={dot.fill}
            css={css`
              animation: ${dot.animation} ${dot.duration}s ease-in-out infinite;
              animation-delay: ${dot.delay}s;
            `}
          />
        ))}
      </svg>
    </div>
    <div className='children position-relative'>
      {children}
    </div>
  </div>
);

export default DotsFlowAnimation;
